import type { DisplayLocation } from '@repo/types';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

interface PendingPrompt {
  text: string;
  displayLocation: DisplayLocation;
  promptId: number;
}

interface TinyAIContextValue {
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  openChatWithPrompt: (prompt: PendingPrompt) => void;
  consumePendingPrompt: () => PendingPrompt | null;
  pendingPromptTrigger: number;
}

const TinyAIContext = createContext<TinyAIContextValue | null>(null);

export const TinyAIProvider = ({ children }: { children: React.ReactNode }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const pendingPromptRef = useRef<PendingPrompt | null>(null);
  const [pendingPromptTrigger, setPendingPromptTrigger] = useState(0);

  const openChat = useCallback(() => {
    setIsChatOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  const openChatWithPrompt = useCallback((prompt: PendingPrompt) => {
    pendingPromptRef.current = prompt;
    setPendingPromptTrigger(n => n + 1);
    setIsChatOpen(true);
  }, []);

  const consumePendingPrompt = useCallback((): PendingPrompt | null => {
    const prompt = pendingPromptRef.current;
    pendingPromptRef.current = null;
    return prompt;
  }, []);

  return (
    <TinyAIContext.Provider
      value={{
        isChatOpen,
        openChat,
        closeChat,
        openChatWithPrompt,
        consumePendingPrompt,
        pendingPromptTrigger,
      }}
    >
      {children}
    </TinyAIContext.Provider>
  );
};

export const useTinyAIContext = (): TinyAIContextValue => {
  const context = useContext(TinyAIContext);
  if (!context) {
    throw new Error('useTinyAIContext must be used within a TinyAIProvider');
  }
  return context;
};
